/*
 * React implementations of the DBBS Cerebellum Hub design-system primitives
 * that the site pages share. Buttons, tabs, sliders etc. come from MUI, styled
 * by src/shared/theme/theme.ts; these cover the patterns MUI has no close match
 * for (MUI's floating-label TextField, for one, cannot take the mono label).
 */
import { useId } from 'react';
import type { CSSProperties, FC, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';
import styles from './components.module.scss';

const cx = (...names: Array<string | false | undefined>) => names.filter(Boolean).join(' ');

// ── Eyebrow ─────────────────────────────────────────────────────────────────

export const Eyebrow: FC<{ children: ReactNode; accent?: boolean; className?: string }> = ({
  children,
  accent = false,
  className,
}) => (
  <span className={cx(styles.eyebrow, accent && styles['eyebrow--accent'], className)}>{children}</span>
);

// ── PageHeading ─────────────────────────────────────────────────────────────

export interface PageHeadingProps {
  title: ReactNode;
  lede?: ReactNode;
  // Tighter spacing, for pages that fit on one screen
  compact?: boolean;
}

// The page name itself is shown in the site header (Header pageTitle)
export const PageHeading: FC<PageHeadingProps> = ({ title, lede, compact = false }) => (
  <div className={cx(styles.pageHeading, compact && styles['pageHeading--compact'])}>
    <h1 className={styles.pageHeading__title}>{title}</h1>
    {lede && <p className={styles.pageHeading__lede}>{lede}</p>}
  </div>
);

// ── PageSurface / PageBody ──────────────────────────────────────────────────

export const PageSurface: FC<{
  children: ReactNode;
  grid?: boolean;
  // With the site header, fill the viewport (the footer starts below it)
  fullHeight?: boolean;
  className?: string;
}> = ({ children, grid = false, fullHeight = false, className }) => (
  <main
    className={cx(
      styles.pageSurface,
      grid && styles['pageSurface--grid'],
      fullHeight && styles['pageSurface--fullHeight'],
      className,
    )}
  >
    {children}
  </main>
);

export const PageBody: FC<{ children: ReactNode; compact?: boolean; className?: string }> = ({
  children,
  compact = false,
  className,
}) => <div className={cx(styles.pageBody, compact && styles['pageBody--compact'], className)}>{children}</div>;

// ── Badge ───────────────────────────────────────────────────────────────────

export type BadgeTone = 'neutral' | 'accent' | 'ok' | 'warn' | 'error' | 'live';

export const Badge: FC<{ tone?: BadgeTone; dot?: boolean; children: ReactNode; className?: string }> = ({
  tone = 'neutral',
  dot = false,
  children,
  className,
}) => (
  <span className={cx(styles.badge, styles[`badge--${tone}`], className)}>
    {dot && <span className={styles.badge__dot} />}
    {children}
  </span>
);

// ── BulletList ──────────────────────────────────────────────────────────────

export const BulletList: FC<{ items: ReactNode[] }> = ({ items }) => (
  <ul className={styles.bulletList}>
    {items.map((item, index) => (
      <li key={index}>{item}</li>
    ))}
  </ul>
);

// ── Card ────────────────────────────────────────────────────────────────────

export interface CardProps {
  title?: ReactNode;
  eyebrow?: ReactNode;
  eyebrowAccent?: boolean;
  media?: string | ReactNode;
  mediaAlt?: string;
  // 'contain' letterboxes logos and icons on the inset ground.
  mediaFit?: 'cover' | 'contain';
  // Width / height of the media slot (16:10 by default)
  mediaAspect?: number;
  badge?: ReactNode;
  footer?: ReactNode;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

// The card panel from the layout spec: media slot, eyebrow, title, body, and
// actions pinned to the foot behind a hairline. Same shape on every page.
export const Card: FC<CardProps> = ({
  title,
  eyebrow,
  eyebrowAccent = false,
  media,
  mediaAlt = '',
  mediaFit = 'cover',
  mediaAspect,
  badge,
  footer,
  disabled = false,
  className,
  style,
  children,
}) => (
  <article className={cx(styles.card, className)} data-disabled={disabled} style={style}>
    {media && (
      <div
        className={cx(styles.card__media, mediaFit === 'contain' && styles['card__media--contain'])}
        style={mediaAspect ? { aspectRatio: mediaAspect } : undefined}
      >
        {typeof media === 'string' ? <img src={media} alt={mediaAlt} /> : media}
        {badge && <span className={styles.card__badge}>{badge}</span>}
      </div>
    )}
    <div className={styles.card__body}>
      {!media && badge}
      {eyebrow && (
        <div className={cx(styles.card__eyebrow, eyebrowAccent && styles['card__eyebrow--accent'])}>{eyebrow}</div>
      )}
      {title && <h3 className={styles.card__title}>{title}</h3>}
      {children && <div className={styles.card__content}>{children}</div>}
      {footer && <div className={styles.card__footer}>{footer}</div>}
    </div>
  </article>
);

export const CardGrid: FC<{ children: ReactNode; columns?: number; className?: string }> = ({
  children,
  columns = 3,
  className,
}) => (
  <div className={cx(styles.cardGrid, className)} style={{ '--card-columns': columns } as CSSProperties}>
    {children}
  </div>
);

// ── Panel ───────────────────────────────────────────────────────────────────

export interface PanelProps {
  title?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  padded?: boolean;
  scroll?: boolean;
  className?: string;
  bodyClassName?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

export const Panel: FC<PanelProps> = ({
  title,
  actions,
  footer,
  padded = true,
  scroll = false,
  className,
  bodyClassName,
  style,
  children,
}) => (
  <section className={cx(styles.panel, className)} style={style}>
    {(title || actions) && (
      <header className={styles.panel__header}>
        <span className={styles.panel__title}>{title}</span>
        {actions && <span className={styles.panel__actions}>{actions}</span>}
      </header>
    )}
    <div
      className={cx(
        styles.panel__body,
        !padded && styles['panel__body--flush'],
        scroll && styles['panel__body--scroll'],
        bodyClassName,
      )}
    >
      {children}
    </div>
    {footer && <footer className={styles.panel__footer}>{footer}</footer>}
  </section>
);

// ── TextInput ───────────────────────────────────────────────────────────────

type FieldBase = {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  className?: string;
};

export type TextInputProps =
  | (FieldBase & { multiline?: false } & InputHTMLAttributes<HTMLInputElement>)
  | (FieldBase & { multiline: true } & TextareaHTMLAttributes<HTMLTextAreaElement>);

export const TextInput: FC<TextInputProps> = (props) => {
  const { label, hint, error, className, multiline, required, id, ...rest } = props as FieldBase & {
    multiline?: boolean;
    required?: boolean;
    id?: string;
  } & Record<string, unknown>;
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = hint || error ? `${inputId}-hint` : undefined;
  const controlProps = {
    id: inputId,
    required,
    className: styles.field__control,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': hintId,
    ...rest,
  };

  return (
    <div className={cx(styles.field, className)}>
      {label && (
        <label htmlFor={inputId} className={styles.field__label}>
          {label}
          {required && <span className={styles.field__required}> *</span>}
        </label>
      )}
      {multiline ? (
        <textarea {...(controlProps as TextareaHTMLAttributes<HTMLTextAreaElement>)} />
      ) : (
        <input {...(controlProps as InputHTMLAttributes<HTMLInputElement>)} />
      )}
      {(hint || error) && (
        <span id={hintId} className={cx(styles.field__hint, !!error && styles['field__hint--error'])}>
          {error || hint}
        </span>
      )}
    </div>
  );
};
